import { useT } from "@transifex/react";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import LoadingSpinner from "../../basic/loadingSpinner/LoadingSpinner";
import ToastMessage from "../../basic/toastMessage/ToastMessage";
import { ActivitiesSection } from "../../complex/classroomDashboard/ActivitiesSection";
import ClassMemberActivityCard from "../../complex/classroomDashboard/ClassMemberActivityCard";
import ClassroomPreviewBanner from "../../complex/classroomDashboard/ClassroomPreviewBanner";
import { ClassroomTopBar } from "../../complex/classroomDashboard/ClassroomTopBar";
import { ClassroomWelcome } from "../../complex/classroomDashboard/ClassroomWelcome";
import { EvaluationsSection } from "../../complex/classroomDashboard/EvaluationsSection";
import { OlderActivitiesSection } from "../../complex/classroomDashboard/OlderActivitiesSection";
import PreviewBlockedPopup from "../../complex/classroomDashboard/PreviewBlockedPopup";
import { useClassMemberActivities } from "../../complex/classroomDashboard/useClassMemberActivities";
import VocabularyTrainerCard from "../../complex/classroomDashboard/VocabularyTrainerCard";
import LegalFooter from "../../complex/legalFooter/LegalFooter";
import { useClassMember } from "../../context/ClassMemberContext";
import { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { ToastVariants } from "../../enums/toast";
import { ClassMemberDashboardItemKinds, type ClassMemberDashboardItem } from "../../helpers/ClassMemberActivities";
import { getClassDetailsExitPreviewRoute } from "../../helpers/navigationHelper";
import { getClassroomRootRoute, getClassroomRoute, getTeacherSubline } from "../../helpers/schoolClassHelpers";
import useToast from "../../hooks/useToast";
import type { TeacherInfo } from "../../networking/schoolClasses";
import type { ClassMemberActivitiesSource } from "../../networking/tests";
import ContentWithHeader, { LogoVariants } from "../../wrapper/ContentWithHeader/ContentWithHeader";

interface ClassroomDashboardProps {
    variant: SchoolClassVisibilities;
    schoolClassId: string;
    schoolClassName: string;
    teacher: TeacherInfo | null;
    memberName?: string;
    isPreview: boolean;
}

const contentMaxWidthStyle = { maxWidth: `${process.env.REACT_APP_UPPER_GRID_STEP_BORDER}px` };

const ClassroomDashboard = (props: ClassroomDashboardProps) => {
    const t = useT();
    const history = useHistory();
    const { member, logout } = useClassMember();

    const isPublic = props.variant === SchoolClassVisibilities.PUBLIC;

    // Private classes look up activities by the logged-in member; public classes (no member) look them
    // up by the class token instead.
    const activitiesSource: ClassMemberActivitiesSource | null = isPublic
        ? { classToken: props.schoolClassId }
        : member?.id
        ? { classMemberId: member.id }
        : null;
    const { current, evaluations, older, isLoading, hasError } = useClassMemberActivities(
        activitiesSource,
        props.schoolClassId
    );

    const [isBlockedPopupOpen, setIsBlockedPopupOpen] = useState(false);

    const loadErrorToast = useToast();
    const triggerLoadErrorToast = loadErrorToast.triggerToast;

    const t_loadFailed = t("The activities could not be loaded. Please try again later.", {
        _tags: "ClassMemberDashboard, ClassroomDashboard",
    });

    useEffect(() => {
        if (hasError) triggerLoadErrorToast();
    }, [hasError, triggerLoadErrorToast]);

    // Random roll once per mount; multiplied by sublines.length below so the array stays the
    // single source of truth — adding/removing a subline can't silently produce undefined.
    const [privateSublineRoll] = useState(() => Math.random());

    // No logout() here: nulling the member would make ClassroomShell redirect to the login page,
    // racing this navigation. ClassDetails clears the session when it detects the exit indicator.
    // replace (not push) so browser Back skips the dead preview entry, whose session is gone.
    const handleExitPreview = () => {
        history.replace(getClassDetailsExitPreviewRoute(props.schoolClassId));
    };

    const handleLogout = () => {
        if (props.isPreview) {
            handleExitPreview();
            return;
        }
        logout();
        history.push(getClassroomRootRoute());
    };

    // Stable identity: in preview the memo()-wrapped cards receive this handler and can skip
    // re-renders. Outside preview guardInPreview returns the call sites' inline arrows, so
    // memoization doesn't hold on that path.
    const openBlockedPopup = useCallback(() => setIsBlockedPopupOpen(true), []);

    // In preview every activity interaction is intercepted: the popup explains instead of navigating.
    const guardInPreview = (action: () => void) => (props.isPreview ? openBlockedPopup : action);

    const renderWelcome = () => {
        if (isPublic) {
            return (
                <ClassroomWelcome
                    title={t("Classroom {className}", {
                        _tags: "ClassMemberDashboard, ClassroomWelcome",
                        className: props.schoolClassName,
                    })}
                    subline={getTeacherSubline(props.teacher, t)}
                />
            );
        }
        // Literal t() calls per subline so Transifex can statically extract all source strings.
        const sublines = [
            t("Let's get started!", { _tags: "ClassMemberDashboard, ClassroomWelcome" }),
            t("Welcome back!", { _tags: "ClassMemberDashboard, ClassroomWelcome" }),
            t("Glad you're here!", { _tags: "ClassMemberDashboard, ClassroomWelcome" }),
            t("Time to get to work!", { _tags: "ClassMemberDashboard, ClassroomWelcome" }),
            t("Ready to learn?", { _tags: "ClassMemberDashboard, ClassroomWelcome" }),
            t("Let's go!", { _tags: "ClassMemberDashboard, ClassroomWelcome" }),
        ];
        return (
            <ClassroomWelcome
                title={t("Hello {name},", {
                    _tags: "ClassMemberDashboard, ClassroomWelcome",
                    name: props.memberName,
                })}
                subline={sublines[Math.floor(privateSublineRoll * sublines.length)]}
            />
        );
    };

    const renderActivityCards = (items: ClassMemberDashboardItem[]) => (
        <div className="flex flex-col gap-s w-full">
            {items.map((item) => {
                if (item.kind === ClassMemberDashboardItemKinds.VOCAB) {
                    return (
                        <VocabularyTrainerCard
                            key={item.activity.testUuid}
                            trainingId={item.activity.testUuid}
                            name={item.activity.name}
                            wordCount={item.activity.wordCount ?? undefined}
                            // "Aufgegeben am" = scheduled date; always set here, since bucketing
                            // hides vocab items without a visibilityDate.
                            assignedDate={item.activity.visibilityDate ?? undefined}
                            shareLink={item.activity.shareLink || undefined}
                            isBlocked={props.isPreview}
                            onLearnClick={props.isPreview ? openBlockedPopup : undefined}
                        />
                    );
                }
                const sharingId = item.result?.sharingId;
                return (
                    <ClassMemberActivityCard
                        key={item.activity.testId}
                        activityId={item.activity.testId}
                        shareMode={item.activity.shareMode}
                        status={item.status}
                        name={item.activity.name}
                        timeLimit={item.activity.timeLimit}
                        dueDate={item.activity.dueDate}
                        // urlBeforePreview belongs to the test-preview flow on /student/:id
                        // (unrelated to classroom preview mode): it sends an owning teacher back
                        // here on "End Preview" and stays inert for students.
                        onStart={guardInPreview(() =>
                            history.push(`/student/${item.activity.testId}`, {
                                urlBeforePreview: getClassroomRoute(props.schoolClassId),
                            })
                        )}
                        onViewResult={
                            sharingId ? guardInPreview(() => history.push(`/result/${sharingId}`)) : undefined
                        }
                    />
                );
            })}
        </div>
    );

    // Cards for the Aktivitäten section, or undefined so the section falls back to its empty state.
    const renderActivitiesChildren = () => (current.length > 0 ? renderActivityCards(current) : undefined);

    // The LegalFooter (a sibling of <main>) stays outside the orange preview border on purpose —
    // moving it inside would cost its contentinfo landmark.
    const previewBorderClass = props.isPreview ? "border-4 border-p6-orange box-border" : "";

    const renderPreviewBanner = () => {
        if (!props.isPreview) return null;
        return <ClassroomPreviewBanner onExit={handleExitPreview} />;
    };

    if (isLoading) {
        return (
            <ContentWithHeader
                logoVariant={LogoVariants.PHASE6}
                isFullWidth
                hasGrayBackground
                noPadding
            >
                <div className="flex flex-col min-h-[calc(100vh-60px)] w-full">
                    <div className="flex flex-1 items-center justify-center w-full">
                        <LoadingSpinner />
                    </div>
                </div>
            </ContentWithHeader>
        );
    }

    return (
        <ContentWithHeader
            logoVariant={LogoVariants.PHASE6}
            isFullWidth
            hasGrayBackground
            noPadding
            footer={<LegalFooter />}
        >
            <div
                className={`flex flex-1 flex-col w-full ${previewBorderClass}`}
                data-testid="classroom-dashboard"
                data-variant={props.variant}
            >
                {renderPreviewBanner()}
                <div className={`flex flex-1 flex-col w-full gap-l pb-xl ${isPublic ? "pt-xl" : ""}`}>
                    {!isPublic && (
                        <div className="px-m p-s">
                            <ClassroomTopBar
                                schoolClassName={props.schoolClassName}
                                teacher={props.teacher}
                                onLogout={handleLogout}
                            />
                        </div>
                    )}

                    <div className="flex flex-1 flex-col gap-xl w-full">
                        <div
                            className="mx-auto w-full flex flex-col gap-xl px-m"
                            style={contentMaxWidthStyle}
                        >
                            {renderWelcome()}
                            <ActivitiesSection>{renderActivitiesChildren()}</ActivitiesSection>
                        </div>
                        {!isPublic && evaluations.length > 0 && (
                            <div className="w-full bg-white py-xl">
                                <div
                                    className="mx-auto w-full px-m"
                                    style={contentMaxWidthStyle}
                                >
                                    <EvaluationsSection>{renderActivityCards(evaluations)}</EvaluationsSection>
                                </div>
                            </div>
                        )}
                        {older.length > 0 && (
                            <div
                                className="mx-auto w-full px-m"
                                style={contentMaxWidthStyle}
                            >
                                <OlderActivitiesSection>{renderActivityCards(older)}</OlderActivitiesSection>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <PreviewBlockedPopup
                isOpen={isBlockedPopupOpen}
                onClose={() => setIsBlockedPopupOpen(false)}
            />

            <ToastMessage
                isOpen={loadErrorToast.isOpen}
                onOpenChange={loadErrorToast.onOpenChange}
                text={t_loadFailed}
                variant={ToastVariants.ERROR}
            />
        </ContentWithHeader>
    );
};

export default ClassroomDashboard;
